const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000'

const quotes = {
    namespaced: true,
    state: {
        quotes: [],
        likedQuoteIds: [],
        loading: false,
        error: null
    },
    mutations: {
        setQuotes(state, quotes) {
            state.quotes = quotes
        },
        addQuote(state, quote) {
            state.quotes.unshift(quote)
        },
        setLikedQuotes(state, likedQuoteIds) {
            state.likedQuoteIds = likedQuoteIds
        },
        toggleLike(state, { quoteId, isLiked, updatedQuote }) {
            const quote = state.quotes.find(quote => quote.id === quoteId)
            if (quote) {
                // Update the like count from the server response
                quote.likes = updatedQuote.likes
                
                // Update liked state
                if (isLiked) {
                    if (!state.likedQuoteIds.includes(quoteId)) {
                        state.likedQuoteIds.push(quoteId)
                    }
                } else {
                    state.likedQuoteIds = state.likedQuoteIds.filter(id => id !== quoteId)
                }
            }
        },
        setLoading(state, loading) {
            state.loading = loading
        },
        setError(state, error) {
            state.error = error
        }
    },
    actions: {
        async fetchQuotes({ commit }) {
            commit('setLoading', true)
            commit('setError', null)
            try {
                const response = await fetch(`${API_URL}/api/quotes`)
                if (!response.ok) throw new Error('Failed to fetch quotes')
                const data = await response.json()
                // Map API response to frontend format
                const quotes = data.map(q => ({
                    id: q.id,
                    title: q.title,
                    quote: q.quoteText,
                    likes: q.likes,
                    author: q.author
                }))
                commit('setQuotes', quotes)
            } catch (error) {
                commit('setError', error.message)
                console.error('Error fetching quotes:', error)
            } finally {
                commit('setLoading', false)
            }
        },
        async add({ commit, rootState }, quote) {
            commit('setLoading', true)
            commit('setError', null)
            try {
                const headers = {
                    'Content-Type': 'application/json',
                }
                
                // Add auth token if available
                if (rootState.auth.token) {
                    headers['Authorization'] = `Bearer ${rootState.auth.token}`
                }
                
                const response = await fetch(`${API_URL}/api/quotes`, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({
                        title: quote.title,
                        quoteText: quote.quote,
                        author: quote.author || null,
                        likes: 0
                    })
                })
                if (!response.ok) {
                    throw new Error(`Failed to submit quote (${response.status})`)
                }
                const data = await response.json()
                // Map API response to frontend format
                const newQuote = {
                    id: data.id,
                    title: data.title,
                    quote: data.quoteText,
                    likes: data.likes,
                    author: data.author
                }
                commit('addQuote', newQuote)
                return newQuote
            } catch (error) {
                commit('setError', error.message)
                console.error('Error submitting quote:', error)
                throw error
            } finally {
                commit('setLoading', false)
            }
        },
        async fetchLikedQuotes({ commit, rootState }) {
            if (!rootState.auth.token) return
            
            try {
                const response = await fetch(`${API_URL}/api/quotes/liked`, {
                    headers: {
                        'Authorization': `Bearer ${rootState.auth.token}`
                    }
                })
                if (response.ok) {
                    const likedQuoteIds = await response.json()
                    commit('setLikedQuotes', likedQuoteIds)
                }
            } catch (error) {
                console.error('Error fetching liked quotes:', error)
            }
        },
        async like({ commit, rootState }, id) {
            try {
                const headers = {}
                
                // Add auth token if available
                if (rootState.auth.token) {
                    headers['Authorization'] = `Bearer ${rootState.auth.token}`
                }
                
                const response = await fetch(`${API_URL}/api/quotes/${id}/like`, {
                    method: 'PUT',
                    headers: headers
                })
                if (!response.ok) {
                    throw new Error(`Failed to like quote (${response.status})`)
                }
                const data = await response.json()
                // Map API response to frontend format
                const updatedQuote = {
                    id: data.quote.id,
                    title: data.quote.title,
                    quote: data.quote.quoteText,
                    likes: data.quote.likes,
                    author: data.quote.author
                }
                commit('toggleLike', { quoteId: id, isLiked: data.isLiked, updatedQuote })
                return data.isLiked
            } catch (error) {
                console.error('Error liking quote:', error)
                throw error
            }
        }
    },
    getters: {
        get: (state) => (id) => {
            return state.quotes.find(quote => quote.id === id)
        },
        isLiked: (state) => (id) => {
            return state.likedQuoteIds.includes(id)
        },
        getLatest: (state) => (amount) => {
            return state.quotes.slice().sort((a, b) => b.id - a.id).slice(0, amount)
        },
        getBest: (state) => (amount) => {
            return state.quotes.slice().sort((a, b) => b.likes - a.likes).slice(0, amount)
        },
        getRandomId: (state) => () => {
            const ids = state.quotes.map(quote => quote.id)
            return ids[Math.floor(Math.random() * ids.length)]
        }
    }
}

export default quotes